import { cConst, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Magic library (plan §4.1 expansion): spells, auras and arcana. The looping
 * effects lean on the ring emitter's `up`/`inward` directions plus
 * vortex/attractor forces for the signature "orbiting energy" reads; the
 * one-shots are choreographed build-up → payoff on the layer timeline.
 */
/** Looping ground portal: pulsing rim, swirling rising wisps, sparkle motes over a glow pool. */
export const arcanePortalEffect = {
    version: 1,
    meta: { name: 'Arcane Portal', duration: 3, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'ap_rim',
            name: 'portal rim',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(3),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.5),
                    rotation: vConst(0),
                    color: cConst([0.7, 0.4, 1, 1]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    // breathing rim: swells twice per cycle
                    width: {
                        preset: 'custom',
                        points: [
                            { t: 0, v: 0.45, outTan: 0 },
                            { t: 0.25, v: 0.7, inTan: 0, outTan: 0 },
                            { t: 0.5, v: 0.45, inTan: 0, outTan: 0 },
                            { t: 0.75, v: 0.7, inTan: 0, outTan: 0 },
                            { t: 1, v: 0.45, inTan: 0 },
                        ],
                    },
                    widthScale: 0.34,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('one') },
                { type: 'rotationOverLife', enabled: true, curve: curvePreset('linear'), scale: 120 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.85]], [0.5, [1, 0.8, 1, 1]], [1, [1, 1, 1, 0.85]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'ap_wisps',
            name: 'swirling wisps',
            delay: 0,
            duration: 3,
            maxParticles: 72,
            emitter: {
                mode: { type: 'rate', perSecond: 24 },
                shape: { type: 'ring', radius: 1.35, direction: 'up' },
                initial: {
                    lifetime: vRange(1.1, 1.8),
                    speed: vRange(0.5, 1),
                    spread: vConst(8),
                    size: vRange(0.05, 0.13),
                    rotation: vConst(0),
                    color: cRange([0.75, 0.5, 1, 1], [0.45, 0.25, 0.95, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.3, 0.6) },
            modules: [
                { type: 'vortex', enabled: true, strength: 2.6 },
                { type: 'drag', enabled: true, coefficient: 0.4 },
                { type: 'turbulence', enabled: true, frequency: 0.9, amplitude: 0.8, scrollSpeed: 0.25 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [0.75, [1, 0.9, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'ap_sparkles',
            name: 'sparkle motes',
            delay: 0,
            duration: 3,
            maxParticles: 24,
            emitter: {
                mode: { type: 'rate', perSecond: 7 },
                shape: { type: 'sphere', radius: 0.6 },
                initial: {
                    lifetime: vRange(1, 1.6),
                    speed: vRange(0.1, 0.3),
                    spread: vConst(0),
                    size: vRange(0.05, 0.12),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-120, 120),
                    color: cRange([1, 0.9, 1, 1], [0.75, 0.6, 1, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -1.1 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.3, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'ap_pool',
            name: 'glow pool',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(3),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.6),
                    rotation: vConst(0),
                    color: cConst([0.4, 0.2, 0.8, 0.5]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.7]], [0.5, [1.2, 1, 1.4, 1]], [1, [1, 1, 1, 0.7]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/** Looping healing aura: decelerating green-gold motes rise in a column, soft pulses on the ground. */
export const healingAuraEffect = {
    version: 1,
    meta: { name: 'Healing Aura', duration: 2.4, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'he_motes',
            name: 'rising motes',
            delay: 0,
            duration: 2.4,
            maxParticles: 48,
            emitter: {
                mode: { type: 'rate', perSecond: 16 },
                shape: { type: 'ring', radius: 0.85, direction: 'up' },
                initial: {
                    lifetime: vRange(1.2, 1.8),
                    speed: vRange(0.9, 1.5),
                    spread: vConst(10),
                    size: vRange(0.06, 0.14),
                    rotation: vConst(0),
                    color: cRange([0.6, 1, 0.6, 1], [0.85, 1, 0.5, 1]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 0.9 },
                { type: 'turbulence', enabled: true, frequency: 0.8, amplitude: 0.5, scrollSpeed: 0.2 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [0.8, [1, 1, 0.85, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'he_crosses',
            name: 'sparkle glints',
            delay: 0,
            duration: 2.4,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 5 },
                shape: { type: 'sphere', radius: 0.6 },
                initial: {
                    lifetime: vRange(0.9, 1.4),
                    speed: vRange(0.05, 0.15),
                    spread: vConst(0),
                    size: vRange(0.08, 0.16),
                    rotation: vRange(-20, 20),
                    color: cRange([1, 1, 0.9, 1], [0.75, 1, 0.75, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.7 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.35, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'he_rings',
            name: 'soft pulses',
            delay: 0,
            duration: 1.3,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1, cycles: 2, interval: 1.2 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1.1),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.25),
                    rotation: vConst(0),
                    color: cConst([0.65, 1, 0.7, 0.75]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 64,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.5, outTan: 0 }, { t: 1, v: 0.12, inTan: -0.5 }] },
                    widthScale: 0.22,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'he_pool',
            name: 'ground glow',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2.4),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.1),
                    rotation: vConst(0),
                    color: cConst([0.35, 0.8, 0.4, 0.45]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.7]], [0.5, [1.15, 1.15, 1, 1]], [1, [1, 1, 1, 0.7]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/** Frost nova: icy flash, expanding frost ring, crystal shards that lock in place, ground rime and mist. */
export const frostNovaEffect = {
    version: 1,
    meta: { name: 'Frost Nova', duration: 1.3, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'fr_flash',
            name: 'ice flash',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.16),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.2),
                    rotation: vConst(0),
                    color: cConst([0.9, 0.98, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.5, 0.8, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'fr_ring',
            name: 'frost wave',
            delay: 0.02,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.6),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(3),
                    rotation: vConst(0),
                    color: cConst([0.75, 0.92, 1, 0.95]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.9, outTan: 0 }, { t: 1, v: 0.15, inTan: -0.9 }] },
                    widthScale: 0.4,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [0.8, 0.95, 1, 0.7]], [1, [0.5, 0.75, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'fr_shards',
            name: 'ice crystals',
            delay: 0.03,
            duration: 0.05,
            maxParticles: 20,
            emitter: {
                mode: { type: 'burst', count: 13 },
                shape: { type: 'ring', radius: 0.5 },
                initial: {
                    lifetime: vRange(0.7, 1.05),
                    speed: vRange(2.5, 4.5),
                    spread: vConst(16),
                    size: vRange(0.14, 0.3),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-160, 160),
                    color: cRange([0.85, 0.96, 1, 1], [0.55, 0.8, 1, 1]),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'shard' } },
            modules: [
                // crystals shoot out then freeze in place
                { type: 'drag', enabled: true, coefficient: 5 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.75, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.4 } },
        }),
        defaultLayer({
            id: 'fr_mist',
            name: 'cold mist',
            delay: 0.05,
            duration: 0.08,
            maxParticles: 10,
            emitter: {
                mode: { type: 'burst', count: 6 },
                shape: { type: 'ring', radius: 0.8 },
                initial: {
                    lifetime: vRange(0.7, 1.1),
                    speed: vRange(0.9, 1.7),
                    spread: vConst(14),
                    size: vRange(0.7, 1.1),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-40, 40),
                    color: cConst([0.8, 0.9, 1, 0.55]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 2.2 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'fr_sparkles',
            name: 'ice glints',
            delay: 0.08,
            duration: 0.1,
            maxParticles: 26,
            emitter: {
                mode: { type: 'burst', count: 18 },
                shape: { type: 'sphere', radius: 1.15, shell: true },
                initial: {
                    lifetime: vRange(0.35, 0.7),
                    speed: vRange(0.2, 0.6),
                    spread: vConst(0),
                    size: vRange(0.05, 0.12),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-240, 240),
                    color: cRange([1, 1, 1, 1], [0.65, 0.9, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'fr_rime',
            name: 'ground rime',
            delay: 0.03,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1.2),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.4),
                    rotation: vRange(0, 360),
                    color: cConst([0.7, 0.88, 1, 0.5]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.15, [1, 1, 1, 1]], [0.6, [1, 1, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: {
                blend: 'additive',
                sprite: { type: 'builtin', id: 'glow' },
                features: {
                    dissolve: {
                        curve: { preset: 'custom', points: [{ t: 0, v: 0.55, outTan: 0 }, { t: 0.5, v: 0.35, inTan: 0, outTan: 0.4 }, { t: 1, v: 1, inTan: 2 }] },
                        edgeWidth: 0.1,
                        edgeColor: [0.8, 0.95, 1, 1],
                        noiseScale: 3,
                    },
                },
            },
        }),
    ],
};
/** Lightning strike: strobing bolt streaks, blinding flash, ground shock ring and crackling residual arcs. */
export const lightningStrikeEffect = {
    version: 1,
    meta: { name: 'Lightning Strike', duration: 0.9, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'li_bolt',
            name: 'main bolt',
            delay: 0,
            duration: 0.04,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.22),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(5),
                    rotation: vConst(90),
                    color: cConst([0.9, 0.95, 1, 1]),
                },
            },
            modules: [
                {
                    // strobe: the bolt re-flashes twice before dying
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.3, [0.6, 0.7, 1, 0.35]], [0.45, [1, 1, 1, 0.9]], [0.7, [0.5, 0.6, 1, 0.25]], [0.82, [1, 1, 1, 0.7]], [1, [0.4, 0.5, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'streak' } },
        }),
        defaultLayer({
            id: 'li_bolt2',
            name: 'secondary bolt',
            delay: 0.05,
            duration: 0.04,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.16),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(3.6),
                    rotation: vRange(78, 102),
                    color: cConst([0.75, 0.85, 1, 0.9]),
                },
            },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.5, [0.7, 0.8, 1, 0.4]], [0.7, [1, 1, 1, 0.8]], [1, [0.4, 0.5, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'streak' } },
        }),
        defaultLayer({
            id: 'li_flash',
            name: 'impact flash',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.2),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.6),
                    rotation: vConst(0),
                    color: cConst([0.95, 0.97, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.45, 0.6, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'li_ring',
            name: 'ground shock',
            delay: 0.02,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.4),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.2),
                    rotation: vConst(0),
                    color: cConst([0.8, 0.9, 1, 0.95]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 64,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.55, outTan: 0 }, { t: 1, v: 0.08, inTan: -0.7 }] },
                    widthScale: 0.26,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.4, 0.55, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'li_arcs',
            name: 'residual arcs',
            delay: 0.04,
            duration: 0.06,
            maxParticles: 30,
            emitter: {
                mode: { type: 'burst', count: 20 },
                shape: { type: 'cone', angle: 65, radius: 0.25 },
                initial: {
                    lifetime: vRange(0.25, 0.55),
                    speed: vRange(4, 8),
                    spread: vConst(10),
                    size: vRange(0.04, 0.1),
                    rotation: vConst(0),
                    color: cRange([0.95, 1, 1, 1], [0.6, 0.75, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.3, 0.55) },
            modules: [
                { type: 'gravity', enabled: true, strength: 11 },
                { type: 'drag', enabled: true, coefficient: 1.8 },
                { type: 'turbulence', enabled: true, frequency: 2.2, amplitude: 9, scrollSpeed: 1.2 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.4, [0.8, 0.9, 1, 0.9]], [0.6, [1, 1, 1, 0.4]], [0.75, [0.9, 0.95, 1, 0.9]], [1, [0.4, 0.55, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
/** Choreographed summon: a rune circle etches itself in, motes stream upward, then the payoff burst fires at 1.9s. */
export const summoningCircleEffect = {
    version: 1,
    meta: { name: 'Summoning Circle', duration: 2.6, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'su_decal',
            name: 'rune circle',
            delay: 0,
            duration: 0.05,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2.4),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.3),
                    rotation: vConst(0),
                    color: cConst([0.7, 0.35, 1, 0.9]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.9]], [0.72, [1.25, 1.1, 1.4, 1]], [0.82, [1.6, 1.5, 1.7, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: {
                blend: 'additive',
                sprite: { type: 'builtin', id: 'glow' },
                features: {
                    // etches in from noise (threshold falls), burns away after the payoff
                    dissolve: {
                        curve: {
                            preset: 'custom',
                            points: [
                                { t: 0, v: 1, outTan: -3 },
                                { t: 0.35, v: 0.25, inTan: -0.5, outTan: 0 },
                                { t: 0.75, v: 0.25, inTan: 0, outTan: 0 },
                                { t: 1, v: 1, inTan: 4 },
                            ],
                        },
                        edgeWidth: 0.14,
                        edgeColor: [0.9, 0.6, 1, 1],
                        noiseScale: 4,
                    },
                },
            },
        }),
        defaultLayer({
            id: 'su_rim',
            name: 'circle rim',
            delay: 0.1,
            duration: 0.05,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2.1),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.15),
                    rotation: vConst(0),
                    color: cConst([0.8, 0.5, 1, 1]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.3, outTan: 0.3 }, { t: 0.75, v: 0.55, inTan: 0.2, outTan: 0 }, { t: 1, v: 0.1, inTan: -3 }] },
                    widthScale: 0.26,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                { type: 'rotationOverLife', enabled: true, curve: curvePreset('easeIn'), scale: 260 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [0.78, [1.2, 1, 1.35, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'su_motes',
            name: 'rising motes',
            delay: 0.3,
            duration: 1.5,
            maxParticles: 56,
            emitter: {
                mode: { type: 'rate', perSecond: 26 },
                shape: { type: 'ring', radius: 1.05, direction: 'up' },
                initial: {
                    lifetime: vRange(0.7, 1.2),
                    speed: vRange(0.8, 1.6),
                    spread: vConst(6),
                    size: vRange(0.05, 0.11),
                    rotation: vConst(0),
                    color: cRange([0.85, 0.6, 1, 1], [0.55, 0.3, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.3, 0.5) },
            modules: [
                { type: 'vortex', enabled: true, strength: 1.8 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'su_payoff_flash',
            name: 'payoff flash',
            delay: 1.9,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.25),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(3),
                    rotation: vConst(0),
                    color: cConst([1, 0.9, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.7, 0.4, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'su_payoff_pillar',
            name: 'light pillar',
            delay: 1.9,
            duration: 0.04,
            maxParticles: 30,
            emitter: {
                mode: { type: 'burst', count: 22 },
                shape: { type: 'ring', radius: 0.35, direction: 'up' },
                initial: {
                    lifetime: vRange(0.35, 0.65),
                    speed: vRange(5, 9),
                    spread: vConst(4),
                    size: vRange(0.07, 0.16),
                    rotation: vConst(0),
                    color: cRange([1, 0.95, 1, 1], [0.75, 0.5, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.4, 0.7) },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.4 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 1, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'su_payoff_ring',
            name: 'payoff shockwave',
            delay: 1.92,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.5),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.8),
                    rotation: vConst(0),
                    color: cConst([0.85, 0.6, 1, 0.95]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 72,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.8, outTan: 0 }, { t: 1, v: 0.1, inTan: -1 }] },
                    widthScale: 0.4,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.6, 0.3, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
    ],
};
/** Looping poison cloud: toon-banded miasma, popping bubbles, dripping ichor over a sickly glow. */
export const poisonCloudEffect = {
    version: 1,
    meta: { name: 'Poison Cloud', duration: 2, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'po_cloud',
            name: 'miasma',
            delay: 0,
            duration: 2,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 7 },
                shape: { type: 'sphere', radius: 0.55 },
                initial: {
                    lifetime: vRange(1.3, 1.9),
                    speed: vRange(0.15, 0.4),
                    spread: vConst(0),
                    size: vRange(0.8, 1.3),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-30, 30),
                    color: cRange([0.45, 0.8, 0.3, 0.8], [0.3, 0.6, 0.25, 0.8]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.35 },
                { type: 'turbulence', enabled: true, frequency: 0.7, amplitude: 0.5, scrollSpeed: 0.15 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 0]], [0.15, [1, 1, 0.8, 0.9]], [0.5, [0.85, 1, 0.6, 0.75]], [0.8, [0.6, 0.8, 0.5, 0.45]], [1, [0.5, 0.6, 0.4, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'po_bubbles',
            name: 'popping bubbles',
            delay: 0,
            duration: 2,
            maxParticles: 24,
            emitter: {
                mode: { type: 'rate', perSecond: 9 },
                shape: { type: 'sphere', radius: 0.5 },
                initial: {
                    lifetime: vRange(0.5, 0.9),
                    speed: vRange(0.05, 0.2),
                    spread: vConst(0),
                    size: vRange(0.06, 0.15),
                    rotation: vConst(0),
                    color: cRange([0.75, 1, 0.4, 1], [0.5, 0.9, 0.3, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -1.6 },
                {
                    // swell then pop
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.4, outTan: 1 }, { t: 0.85, v: 1.1, inTan: 0.6, outTan: 0 }, { t: 0.95, v: 1.35, inTan: 3, outTan: -20 }, { t: 1, v: 0, inTan: -25 }] },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.4]], [0.8, [1, 1, 1, 1]], [1, [1, 1, 1, 0.9]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'po_drips',
            name: 'ichor drips',
            delay: 0,
            duration: 2,
            maxParticles: 12,
            emitter: {
                mode: { type: 'rate', perSecond: 4 },
                shape: { type: 'ring', radius: 0.55 },
                initial: {
                    lifetime: vRange(0.4, 0.7),
                    speed: vRange(0.1, 0.3),
                    spread: vConst(20),
                    size: vRange(0.05, 0.1),
                    rotation: vConst(0),
                    color: cRange([0.5, 0.85, 0.25, 1], [0.3, 0.55, 0.2, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.3, 0.5) },
            modules: [
                { type: 'gravity', enabled: true, strength: 5 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.8, [1, 1, 1, 0.9]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'po_glow',
            name: 'sickly glow',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.9),
                    rotation: vConst(0),
                    color: cConst([0.35, 0.65, 0.2, 0.4]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.7]], [0.5, [1.2, 1.2, 1, 1]], [1, [1, 1, 1, 0.7]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/**
 * Looping tesla arc: three jittered beam ribbons crackle between the origin
 * and a point 2.4 units out (+X). Short lifetimes re-strike each arc with a
 * fresh emitter jitter; scrolled noise + tiling makes the energy dance along
 * the ribbon — the Roblox-Beam workout.
 */
const ARC_WIDTH = {
    preset: 'custom',
    points: [
        { t: 0, v: 0.2, outTan: 6 },
        { t: 0.2, v: 1, inTan: 0, outTan: 0 },
        { t: 0.8, v: 1, inTan: 0, outTan: 0 },
        { t: 1, v: 0.2, inTan: -6 },
    ],
};
function teslaArcLayer(id, name, curve0, curve1, widthScale, rate) {
    return defaultLayer({
        id,
        name,
        delay: 0,
        duration: 1.6,
        maxParticles: 6,
        emitter: {
            mode: { type: 'rate', perSecond: rate },
            shape: { type: 'sphere', radius: 0.07 },
            initial: {
                lifetime: vRange(0.07, 0.16), // each arc re-strikes with fresh jitter
                speed: vConst(0),
                spread: vConst(0),
                size: vRange(0.92, 1.05),
                rotation: vConst(0),
                color: cRange([0.8, 0.95, 1, 1], [0.55, 0.75, 1, 1]),
            },
        },
        renderer: { type: 'beam', end: [2.4, 0.35, 0], curve0, curve1, width: ARC_WIDTH, widthScale, segments: 28, tiles: 3 },
        modules: [
            {
                type: 'colorOverLife',
                enabled: true,
                // hot stops compensate for the noise texture multiply (~0.5 average)
                gradient: gradient('stepped', [0, [1.9, 1.95, 2, 1]], [0.5, [1.5, 1.7, 2, 0.8]], [1, [1, 1.3, 1.9, 0.4]]),
            },
        ],
        material: {
            blend: 'additive',
            sprite: { type: 'builtin', id: 'noise' },
            features: { uvScroll: { x: -2.4, y: 0 } },
        },
    });
}
export const teslaArcEffect = {
    version: 1,
    meta: { name: 'Tesla Arc', duration: 1.6, loop: true, simulationSpace: 'local' },
    layers: [
        teslaArcLayer('te_arc_a', 'arc — high bow', [0.1, 0.55, 0.25], [-0.15, 0.35, -0.3], 0.09, 11),
        teslaArcLayer('te_arc_b', 'arc — low bow', [0.2, -0.15, -0.35], [-0.1, 0.5, 0.3], 0.07, 9),
        defaultLayer({
            id: 'te_core',
            name: 'core arc',
            delay: 0,
            duration: 1.6,
            maxParticles: 5,
            emitter: {
                mode: { type: 'rate', perSecond: 12 },
                shape: { type: 'sphere', radius: 0.03 },
                initial: {
                    lifetime: vRange(0.06, 0.12),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.95, 1.02),
                    rotation: vConst(0),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            renderer: { type: 'beam', end: [2.4, 0.35, 0], curve0: [0, 0.3, 0.1], curve1: [0, 0.25, -0.12], width: ARC_WIDTH, widthScale: 0.035, segments: 28 },
            modules: [],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0.2 } },
        }),
        defaultLayer({
            id: 'te_source',
            name: 'source glow',
            delay: 0,
            duration: 1.6,
            maxParticles: 8,
            emitter: {
                mode: { type: 'rate', perSecond: 6 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vRange(0.2, 0.35),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.5, 0.7),
                    rotation: vConst(0),
                    color: cRange([0.85, 0.95, 1, 0.9], [0.6, 0.8, 1, 0.7]),
                },
            },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.4, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'te_sparks',
            name: 'stray sparks',
            delay: 0,
            duration: 1.6,
            maxParticles: 16,
            emitter: {
                mode: { type: 'rate', perSecond: 8 },
                shape: { type: 'edge', length: 4.4 }, // sparks jump off along the arc's span
                initial: {
                    lifetime: vRange(0.15, 0.35),
                    speed: vRange(0.8, 2),
                    spread: vConst(50),
                    size: vRange(0.03, 0.07),
                    rotation: vConst(0),
                    color: cRange([0.9, 1, 1, 1], [0.6, 0.8, 1, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 4 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.5, 0.7, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
/** Looping black hole: matter spirals into a dark core along trailing streams, rim glows violet. */
export const blackHoleEffect = {
    version: 1,
    meta: { name: 'Black Hole', duration: 2.5, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'bh_core',
            name: 'event horizon',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2.5),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(0.85),
                    rotation: vConst(0),
                    color: cConst([0.02, 0, 0.05, 1]),
                },
            },
            modules: [
                {
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.96, outTan: 0.3 }, { t: 0.5, v: 1.04, inTan: 0, outTan: 0 }, { t: 1, v: 0.96, inTan: -0.3 }] },
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'bh_rim',
            name: 'photon rim',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2.5),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.15),
                    rotation: vConst(0),
                    color: cConst([0.65, 0.35, 1, 0.9]),
                },
            },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.8]], [0.5, [1.3, 1.1, 1.4, 1]], [1, [1, 1, 1, 0.8]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'ring' } },
        }),
        defaultLayer({
            id: 'bh_accretion',
            name: 'accretion disc',
            delay: 0,
            duration: 2.5,
            maxParticles: 96,
            emitter: {
                mode: { type: 'rate', perSecond: 36 },
                shape: { type: 'ring', radius: 1.7, direction: 'inward' },
                initial: {
                    lifetime: vRange(0.8, 1.2),
                    speed: vRange(0.4, 0.8),
                    spread: vConst(4),
                    size: vRange(0.05, 0.12),
                    rotation: vConst(0),
                    color: cRange([1, 0.75, 0.4, 1], [0.7, 0.4, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.4, 0.7) },
            modules: [
                { type: 'vortex', enabled: true, strength: 4.2 },
                { type: 'attractor', enabled: true, position: [0, 0, 0], strength: 5 },
                {
                    // brighten as they spiral in, blue-shift, vanish at the horizon
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 0.9]], [0.8, [1.1, 1, 1.3, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'bh_infall',
            name: 'infall trails',
            delay: 0,
            duration: 2.5,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 7 },
                shape: { type: 'sphere', radius: 2.1, shell: true },
                initial: {
                    lifetime: vRange(1, 1.5),
                    speed: vConst(0.3),
                    spread: vConst(0),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cRange([0.85, 0.7, 1, 1], [0.55, 0.4, 1, 1]),
                },
            },
            renderer: {
                type: 'trail',
                points: 18,
                width: { preset: 'custom', points: [{ t: 0, v: 1, outTan: 0 }, { t: 1, v: 0, inTan: -2 }] },
                widthScale: 0.05,
            },
            modules: [
                { type: 'attractor', enabled: true, position: [0, 0, 0], strength: 8 },
                { type: 'vortex', enabled: true, strength: 2.4 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.3, [1, 1, 1, 0.9]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
    ],
};
